/**
 * HIS 工作站注册表
 *
 * 工作站 = 站点类型（clinical 临床场景 / station 岗位站）+ 模块权益 + 角色范围：
 *  - `clinical` 站对应 `ClinicalScene`（门诊/住院/护士），复用现有页面与显示模式；
 *  - `station` 站是独立路由的岗位页面（门诊收费/门诊药房/住院收费/住院药房/健康管理），
 *    只登记**已落地**的站点，避免死链；
 *  - 模块权益由后端 Entitlement 目录投影（`capabilityStore.has`），此表只声明
 *    「该工作站需要哪些模块」，不复制授权判定逻辑；
 *  - 角色范围为该工作站的可进入候选角色（`roleHints`，空表示不限制）；
 *  - 产品差异仍只允许落在 `config/productMode.ts`，此表不得出现产品名分叉。
 *
 * 单一事实源：顶栏工作站下拉、`WorkstationShell` 站点切换、设置面板均从本表派生；
 * 历史 `crossStationNav.ts` 已删除（入口全部并入本表）。
 *
 * @module src/config/workstations
 */

import type { ClinicalScene } from "@shared/stores";
import type { DisplayMode } from "@shared/utils/displayMode";

/** 工作站类型：clinical 临床场景站 / station 岗位站（独立页面） */
export type WorkstationKind = "clinical" | "station";

/** 岗位站编码（独立页面路由，不属于 ClinicalScene） */
export const STATION_CODES = [
  "outpatient_doctor",
  "registration_desk",
  "outpatient_charging",
  "outpatient_pharmacy",
  "inpatient_charging",
  "inpatient_pharmacy",
  "health_management",
  "emr_front_sheet",
  "pharmacy_review",
  "triage_station",
  "inpatient_documents",
  "barcode_verify",
  "quality_dashboard",
  "record_writing",
] as const;

export type StationCode = (typeof STATION_CODES)[number];

/** 系统编码（顶栏左侧系统选择器；系统只做导航分组，不是授权单位） */
export const SYSTEM_CODES = [
  "cdss",
  "outpatient",
  "record_writing",
  "inpatient",
  "health",
  "record_qc",
] as const;

export type SystemCode = (typeof SYSTEM_CODES)[number];

/** 系统定义：名称 + 说明；可见性 = 其下任一工作站可用（权益 ∩ 角色） */
export interface SystemDefinition {
  code: SystemCode;
  name: string;
  description: string;
}

/** 工作站编码：临床场景保持 `ClinicalScene` 兼容，岗位站使用独立编码 */
export type WorkstationCode = ClinicalScene | StationCode;

/** 站内模式（顶栏段控）：clinical 站当前显示模式的声明 */
export interface WorkstationModeDefinition {
  /** 段控稳定标识（与 displayMode 一致，便于测试/埋点） */
  key: string;
  /** 段控文案 */
  label: string;
  /** 站内模式（displayMode）；缺省表示该段为跨路由入口（读 route） */
  displayMode?: DisplayMode;
  /** 跨路由入口的落地路由（displayMode 缺省时使用） */
  route?: string;
}

/** 工作站定义（注册表条目） */
export interface WorkstationDefinition {
  /** 工作站编码；clinical 站同时是 layoutStore.clinicalScene 的取值 */
  code: WorkstationCode;
  /** 所属系统（顶栏系统选择器的分组依据） */
  system: SystemCode;
  /** 站点类型：临床场景站 / 岗位站 */
  kind: WorkstationKind;
  /** 展示名（顶栏/设置面板/外壳） */
  name: string;
  /** 一句话说明（设置面板卡片） */
  description: string;
  /** 进入该工作站所需的模块权益；空数组表示无模块门槛 */
  requiredModules: readonly string[];
  /**
   * 模块权益的「任一满足」清单（与 requiredModules 互斥优先）。
   * 用于同一站点可被不同产品分别开通的场景（如病历书写：record 或 outpatient_record），
   * 因为 requiredModules 是 every（AND）语义，无法表达 OR。
   */
  entitledAnyModules?: readonly string[];
  /** 切换工作站时落地的路由 */
  defaultRoute: string;
  /** 可进入该工作站的候选角色；空数组表示不限制角色 */
  roleHints: readonly string[];
  /** 站内模式（顶栏段控的渲染来源）；空数组表示该站不显示段控 */
  modes: readonly WorkstationModeDefinition[];
  /**
   * 仅从导航（顶栏工作站按钮/系统选择器派生）中隐藏，路由与深链仍可用。
   * 用于功能尚未对外开放但已落地的站点，避免死链与删配置。
   */
  hidden?: boolean;
}

/** 门诊站内模式（段控 3 项）：病历=对话主模式、诊断、医嘱。
 *
 * 「文书」段控已下线：文书工作台入口迁至独立的「病历书写系统」(/record-writing)，
 * displayMode="workspace" 的渲染能力保留（历史深链 ?mode=workspace 仍可用）。
 */
const OUTPATIENT_MODES: readonly WorkstationModeDefinition[] = [
  { key: "full", label: "病历", displayMode: "full" },
  { key: "diagnosis", label: "诊断", displayMode: "diagnosis" },
  { key: "order", label: "医嘱", displayMode: "order" },
];

/** 工作站全集（顺序即顶栏工作站下拉与设置面板的展示顺序） */
export const WORKSTATIONS: readonly WorkstationDefinition[] = [
  {
    code: "outpatient",
    kind: "clinical",
    system: "cdss",
    // 2026-10-31：界面定位由「门诊医生工作站」上移为「决策支持系统」，
    // 站内顶栏段控（门诊/文书/诊断/医嘱）保持不变。
    name: "决策支持系统",
    description: "AI 决策与病历工作台：诊断治疗、理法方药、病历书写与医嘱。",
    requiredModules: [],
    defaultRoute: "/medical",
    roleHints: [],
    modes: OUTPATIENT_MODES,
  },
  {
    code: "outpatient_doctor",
    system: "outpatient",
    kind: "station",
    name: "门诊医生工作站",
    description: "门诊接诊队列、挂号状态与收费/药房/决策支持系统联动。",
    requiredModules: ["outpatient_registration"],
    defaultRoute: "/outpatient",
    roleHints: ["doctor", "dept_admin", "hospital_admin", "super_admin"],
    modes: [],
  },
  {
    code: "registration_desk",
    system: "outpatient",
    kind: "station",
    name: "挂号收费窗口",
    description: "收费员检索或建档后挂号，有收费权益时当场收挂号费。",
    requiredModules: ["outpatient_registration"],
    defaultRoute: "/registration-desk",
    roleHints: ["cashier", "dept_admin", "hospital_admin", "super_admin"],
    modes: [],
  },
  {
    code: "inpatient",
    kind: "clinical",
    system: "inpatient",
    name: "住院医生工作站",
    description: "住院工作台：床位板、入院/转科/出院、住院医嘱与预交金。",
    requiredModules: ["inpatient"],
    defaultRoute: "/inpatient",
    roleHints: [],
    // 住院工作台内部自带业务页签，顶栏不再叠加段控
    modes: [],
  },
  {
    code: "nurse",
    kind: "clinical",
    system: "inpatient",
    name: "住院护士工作站",
    description:
      "护理业务工作台：床位板、跨患者护理工作清单、生命体征、护理评估与护理记录。",
    requiredModules: ["nursing"],
    defaultRoute: "/nurse",
    // 护士站是护理执行台；医生站只读共享患者上下文，不在此列
    roleHints: ["nurse", "dept_admin", "hospital_admin", "super_admin"],
    // 护士工作台内部自带业务页签，顶栏不再叠加段控
    modes: [],
  },
  {
    code: "outpatient_charging",
    kind: "station",
    system: "outpatient",
    name: "门诊收费工作站",
    description: "收费单、收银、退费冲正与日结交班。",
    requiredModules: ["outpatient_charging"],
    defaultRoute: "/charging",
    // 收费岗位站只对收费/管理角色开放入口：医生在门诊站内已有内嵌收费工作台，
    // 顶栏再出现同系统岗位按钮属重复入口（与住院收费站同一口径）。
    roleHints: [
      "inpatient_cashier",
      "cashier",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "outpatient_pharmacy",
    kind: "station",
    system: "outpatient",
    name: "门诊药房工作站",
    description: "待发药队列、发药/退药与库存预警。",
    requiredModules: ["outpatient_pharmacy"],
    defaultRoute: "/pharmacy-ops",
    // 药房岗位站只对药学/管理角色开放入口（与住院药房、药师审方同一口径）
    roleHints: [
      "inpatient_pharmacist",
      "pharmacist",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "inpatient_charging",
    kind: "station",
    system: "inpatient",
    name: "住院收费工作站",
    description: "住院预交金、费用清单、住院收费单与出院结算。",
    requiredModules: ["inpatient_charging"],
    defaultRoute: "/inpatient-charging",
    // 住院收费员及收费/管理角色；最终以后端 RBAC 为准（此处只做入口显隐）
    roleHints: [
      "inpatient_cashier",
      "cashier",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "inpatient_pharmacy",
    kind: "station",
    system: "inpatient",
    name: "住院药房工作站",
    description: "住院摆药/发药/退药、静配中心（PIVAS）与住院药房库存。",
    requiredModules: ["inpatient_pharmacy"],
    defaultRoute: "/inpatient-pharmacy",
    // 住院药师及药学/管理角色；最终以后端 RBAC 为准（此处只做入口显隐）
    roleHints: [
      "inpatient_pharmacist",
      "pharmacist",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "health_management",
    kind: "station",
    system: "health",
    name: "健康管理工作站",
    description:
      "健康管理工作站：健康档案（只读）、风险评估、随访计划与健康教育（业务建设中）。",
    requiredModules: ["health_management", "outpatient_patient"],
    defaultRoute: "/health",
    // 健康管理师/医护/管理角色；最终以后端 RBAC 为准
    roleHints: [
      "health_manager",
      "doctor",
      "nurse",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "emr_front_sheet",
    kind: "station",
    system: "record_qc",
    name: "病案首页录入/编码",
    description: "病案首页录入、编码、确定性质控、归档与打印。",
    requiredModules: ["emr_front_sheet"],
    defaultRoute: "/front-sheet",
    roleHints: [
      "medical_record",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "pharmacy_review",
    kind: "station",
    system: "outpatient",
    name: "药师审方工作站",
    description:
      "待审药品医嘱队列：通过（仍走安全守卫）或驳回（必填理由，驳回后只能作废重开）。",
    requiredModules: ["outpatient_pharmacy"],
    defaultRoute: "/pharmacy-review",
    roleHints: [
      "pharmacist",
      "inpatient_pharmacist",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "triage_station",
    kind: "station",
    system: "outpatient",
    name: "门诊分诊工作站",
    // 2026-11-01：暂不对外暴露入口（深链 /triage-station 仍可用）
    hidden: true,
    description: "号源余量、停号/复号、占号与退号释放，以及门诊四级分诊登记。",
    requiredModules: ["outpatient_registration"],
    defaultRoute: "/triage-station",
    roleHints: [
      "nurse",
      "doctor",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "inpatient_documents",
    kind: "station",
    system: "inpatient",
    name: "住院文书工作站",
    description:
      "入院记录、病程记录、出院记录的书写与签署；出院前文书闭环校验。",
    requiredModules: ["inpatient"],
    defaultRoute: "/inpatient-documents",
    roleHints: [
      "doctor",
      "nurse",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "barcode_verify",
    kind: "station",
    system: "inpatient",
    name: "条码核对工作站",
    description: "患者腕带 / 标本 / 药品扫码核对，核对结果（含拦截）全部留痕。",
    requiredModules: ["nursing"],
    defaultRoute: "/barcode-verify",
    roleHints: [
      "nurse",
      "pharmacist",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "quality_dashboard",
    kind: "station",
    system: "record_qc",
    name: "医疗质量管理工作站",
    description: "质量指标口径、按日计算、快照查询与评级抽数 CSV 导出。",
    // 入口必须要求 quality 模块权益：该站接口全部按 quality 模块门控，
    // 不写模块会使「有角色无权益」的账号看到入口后被后端 403。
    requiredModules: ["quality"],
    defaultRoute: "/quality-dashboard",
    roleHints: [
      "medical_record",
      "dept_admin",
      "hospital_admin",
      "super_admin",
    ],
    modes: [],
  },
  {
    code: "record_writing",
    kind: "station",
    system: "record_writing",
    name: "病历书写工作站",
    description:
      "按患者与文书类型检索文库、审核修改正文、导出 Word 与写入病历。",
    // 病历书写系统横跨两个产品：决策支持（record）与门诊（outpatient_record）。
    // requiredModules 是 every（AND），表达不了「任一」；用 entitledAnyModules 后
    // 两个产品都能进入，且不改变其他工作站的 AND 语义。
    requiredModules: [],
    entitledAnyModules: ["record", "outpatient_record"],
    defaultRoute: "/record-writing",
    roleHints: ["doctor", "dept_admin", "hospital_admin", "super_admin"],
    modes: [],
  },
];

/** 工作站可用性判定的输入：模块权益查询 + 当前用户角色 */
export interface WorkstationGates {
  /** 模块权益查询（通常传 `capabilityStore.has`） */
  has: (moduleCode: string) => boolean;
  /** 当前用户角色（未登录/未知传 null） */
  role?: string | null;
  /** 已授权模块码，用于识别 tcm_* 学派模块 */
  codes?: readonly string[];
}

/** 是否已获得工作站所需模块权益（空模块列表视为已授权） */
export function isWorkstationEntitled(
  station: WorkstationDefinition,
  has: (moduleCode: string) => boolean,
): boolean {
  // 「任一满足」优先：同一站点可能被不同产品分别开通
  if (station.entitledAnyModules?.length) {
    return station.entitledAnyModules.some((moduleCode) => has(moduleCode));
  }
  return station.requiredModules.every((moduleCode) => has(moduleCode));
}

/** 当前角色是否可进入该工作站（roleHints 为空表示不限制角色） */
export function isWorkstationRoleAllowed(
  station: WorkstationDefinition,
  role?: string | null,
): boolean {
  if (station.roleHints.length === 0) return true;
  return Boolean(role) && station.roleHints.includes(role as string);
}

/** 决策支持页面需要至少一个对话页签、文书或诊断权益。 */
export function canEnterCdss(gates: WorkstationGates): boolean {
  const codes = gates.codes ?? [];
  if (codes.some((code) => code === "tcm" || code.startsWith("tcm_"))) {
    return true;
  }
  if (gates.has("workspace") || gates.has("diagnosis")) return true;
  return ["clinical", "tcm", "record", "quality", "health", "chronic"].some(
    (code) => gates.has(code),
  );
}

/** 权益与角色同时满足才可进入工作站 */
export function isWorkstationAvailable(
  station: WorkstationDefinition,
  gates: WorkstationGates,
): boolean {
  if (station.code === "outpatient" && !canEnterCdss(gates)) return false;
  return (
    isWorkstationEntitled(station, gates.has) &&
    isWorkstationRoleAllowed(station, gates.role)
  );
}

/** 当前身份可进入的工作站（保持注册表顺序，clinical + station 全部返回） */
export function listAvailableWorkstations(
  gates: WorkstationGates,
): WorkstationDefinition[] {
  return WORKSTATIONS.filter((station) =>
    isWorkstationAvailable(station, gates),
  );
}

/** 按编码取工作站定义 */
export function getWorkstation(
  code: WorkstationCode,
): WorkstationDefinition | undefined {
  return WORKSTATIONS.find((station) => station.code === code);
}

/** 是否为临床场景站（编码可直接写入 layoutStore.clinicalScene） */
export function isClinicalWorkstation(
  station: WorkstationDefinition | undefined,
): station is WorkstationDefinition & { code: ClinicalScene } {
  return station?.kind === "clinical";
}

/** 取场景的默认落地路由（未知场景回退门诊首页，避免死链） */
export function defaultRouteForScene(code: ClinicalScene): string {
  return getWorkstation(code)?.defaultRoute ?? "/medical";
}

/** 系统全集（顺序即顶栏系统选择器展示顺序） */
export const SYSTEMS: readonly SystemDefinition[] = [
  {
    code: "cdss",
    name: "决策支持系统",
    description: "AI 决策与病历工作台（现状界面：门诊/文书/诊断/医嘱）。",
  },
  {
    code: "outpatient",
    name: "门诊信息系统",
    description: "门诊业务闭环：医生站、收费、药房、分诊、审方。",
  },
  {
    code: "record_writing",
    name: "病历书写系统",
    description: "正式病历文书与 AI 报告的书写、审核、升格与导出归档。",
  },
  {
    code: "inpatient",
    name: "住院信息系统",
    description: "住院业务闭环：医生站、护士站、文书、收费、药房。",
  },
  {
    code: "health",
    name: "健康管理系统",
    description: "建档、风险评估、管理方案与随访。",
  },
  {
    code: "record_qc",
    name: "病案质控系统",
    description: "病案首页、形式/内涵质控、整改追踪与质控报表。",
  },
];

/** 按编码取系统定义 */
export function getSystem(code: SystemCode): SystemDefinition | undefined {
  return SYSTEMS.find((system) => system.code === code);
}

/** 某系统下当前身份可用的工作站（保持注册表顺序；hidden 站不进导航） */
export function listWorkstationsBySystem(
  system: SystemCode,
  gates: WorkstationGates,
): WorkstationDefinition[] {
  return WORKSTATIONS.filter(
    (station) =>
      station.system === system &&
      !station.hidden &&
      isWorkstationAvailable(station, gates),
  );
}

/** 当前身份可见的系统：其下至少一个工作站可用 */
export function listAvailableSystems(
  gates: WorkstationGates,
): SystemDefinition[] {
  return SYSTEMS.filter(
    (system) => listWorkstationsBySystem(system.code, gates).length > 0,
  );
}

/** 系统默认落地工作站：系统内第一个可用站；无可用站返回 undefined */
export function defaultWorkstationForSystem(
  system: SystemCode,
  gates: WorkstationGates,
): WorkstationDefinition | undefined {
  return listWorkstationsBySystem(system, gates)[0];
}

/** 由工作站编码反查系统编码（顶栏系统选择器回显） */
export function systemOfWorkstation(
  code: WorkstationCode,
): SystemCode | undefined {
  return getWorkstation(code)?.system;
}

/**
 * 切入某系统时是否要先重置站内布局：
 * 切回决策支持系统统一回到「门诊」对话主界面，不沿用上次停留的文书/诊断/医嘱布局；
 * 其它系统各有独立页面，与 displayMode 无关，返回 null 表示不改动。
 */
export function landingDisplayModeForSystem(
  code: SystemCode,
): DisplayMode | null {
  return code === "cdss" ? "full" : null;
}
